import { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';
import { AppThemeProvider } from '@/theme/AppThemeProvider';
import { ErrorBoundary } from '@/components/organisms/ErrorBoundary';
import { queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { AppRoutes } from '@/routes/AppRoutes';

/** Styles toasts from the theme so they follow the light/dark switch. */
function ThemedToaster(): JSX.Element {
  const theme = useTheme();

  return (
    <Toaster
      position="bottom-right"
      gutter={8}
      toastOptions={{
        duration: 4000,
        style: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 4,
          boxShadow: theme.customShadows.md,
          fontSize: '0.875rem',
          maxWidth: 420,
        },
        success: { iconTheme: { primary: theme.palette.success.main, secondary: theme.palette.background.paper } },
        error: {
          duration: 7000,
          iconTheme: { primary: theme.palette.error.main, secondary: theme.palette.background.paper },
        },
      }}
    />
  );
}

/** Restores the session from a persisted token before the router renders. */
function SessionBootstrap({ children }: { children: JSX.Element }): JSX.Element {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return children;
}

/**
 * Provider composition. Order matters:
 *  - the theme wraps everything, so error boundaries and toasts are styled;
 *  - React Query sits above the router, since guards read cached session data;
 *  - the router is innermost, because `useAssistant` and pages depend on it.
 */
export function App(): JSX.Element {
  return (
    <AppThemeProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SessionBootstrap>
              <AppRoutes />
            </SessionBootstrap>
          </BrowserRouter>
          <ThemedToaster />
        </QueryClientProvider>
      </ErrorBoundary>
    </AppThemeProvider>
  );
}
